/**
 * The desktop: wallpaper, menu bar, window layer, dock, toasts. Two presenters
 * off one store: absolute windows on desktop, and on phones (≤768px) a mobile
 * home screen (docs/PLAN-2026-09-21-mobile-os-shell.md): system bar, app grid,
 * widgets, dock, App Library, Search, Profile, one app full screen at a time.
 * Its components are ported from @gamo/desk into ./mobile; ./phone.tsx fills
 * them for the person.
 */
import { Suspense, useEffect, useRef, useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useAuth } from "../auth";
import { canSee } from "../lib/permissions";
import { APPS, appById, type RadarAppDefinition } from "../apps/registry";
import { MenuBar } from "./MenuBar";
import { WindowFrame } from "./WindowFrame";
import { RadarMark } from "./RadarMark";
import { useShell } from "./useShell";
import { registerShortcut, useShortcut } from "./shortcuts";
import {
  closeWindow,
  cycleFocus,
  dismissToast,
  focusWindow,
  minimizeWindow,
  openWindow,
  pushToast,
  restoreLayout,
  restoreWindow,
  type WindowState,
} from "./store";
import { useAppLabel } from "../lib/i18n";
import { openOnPhone } from "./mobile/MobileHome";
import type { RadarMobile } from "./mobile/types";
import { RadarPhone } from "./phone";
import {
  addableDockIds,
  addToDock,
  removeFromDock,
  reorderDock,
  resetDock,
  resolveDockIds,
  subscribeDock,
  getDockOrder,
} from "./dock-store";

function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => window.matchMedia("(max-width: 768px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const onChange = () => setMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return mobile;
}

export function launchApp(def: RadarAppDefinition, launchParams?: Record<string, string>) {
  // A phone shows one app at a time (docs/PLAN-2026-09-21-mobile-os-shell.md): opening another from inside an app
  // replaces it, and Back to home closes it.
  if (typeof window !== "undefined" && window.matchMedia?.("(max-width: 768px)").matches) return openOnPhone(def, launchParams);
  openWindow({
    appId: def.id,
    title: def.name,
    defaultBounds: def.defaultBounds,
    singleton: def.singleton,
    launchParams,
  });
}

export interface DesktopProps {
  /**
   * The phone home screen's parts, over what ./phone.tsx assembles for the workspace and the person (the desk's
   * `mobile` prop). Radar itself passes nothing.
   */
  mobile?: Partial<RadarMobile>;
}

export function Desktop({ mobile }: DesktopProps = {}) {
  const shell = useShell();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const role = user?.role;
  const { enginePermissions: permissions } = useAuth();
  // Dock/tab bar/shortcuts: what the user may see (by the engine's permissions) AND isn't hidden
  // (Profile is launchable from the menu, never docked).
  const visibleApps = APPS.filter((a) => !a.hidden && canSee(a.permission, role, permissions));

  // restore persisted layout once. Not on a phone: it opens on its home screen, and the saved layout is a desktop's.
  useEffect(() => {
    if (!window.matchMedia("(max-width: 768px)").matches) restoreLayout();
  }, []);

  // Deep links: `#/<appId>?k=v` (bell + task links) opens that app with the
  // query as launchParams, then clears the hash so a refresh stays clean.
  useEffect(() => {
    const handle = () => {
      const h = window.location.hash;
      if (!h.startsWith("#/")) return;
      const [path, query = ""] = h.slice(2).split("?");
      const def = appById(path?.split("/")[0] ?? "");
      if (!def) return;
      const params: Record<string, string> = {};
      for (const [k, v] of new URLSearchParams(query)) params[k] = v;
      launchApp(def, params);
      // The entry's state is kept: on a phone it may carry an open layer's mark (shell/mobile/layers.ts), and wiping it
      // would leave Back with nothing to close.
      history.replaceState(history.state, "", window.location.pathname + window.location.search);
    };
    handle();
    window.addEventListener("hashchange", handle);
    return () => window.removeEventListener("hashchange", handle);
  }, []);

  const focusedId = shell.stack.at(-1);
  const focusedWin = focusedId ? shell.windows[focusedId] : undefined;
  const focusedApp = focusedWin ? appById(focusedWin.appId) : undefined;

  // global shortcuts
  useShortcut({ combo: "mod+w", scope: "global", label: "Close window", run: () => focusedId && closeWindow(focusedId) }, [focusedId]);
  useShortcut({ combo: "mod+m", scope: "global", label: "Minimize window", run: () => focusedId && minimizeWindow(focusedId) }, [focusedId]);
  useShortcut({ combo: "mod+`", scope: "global", label: "Cycle windows", run: cycleFocus }, []);
  useEffect(() => {
    // ⌘1..9 dock launch — imperative registration because the count varies by role
    const offs = visibleApps.slice(0, 9).map((def, i) => registerDockShortcut(def, i));
    return () => offs.forEach((off) => off());
    // keyed on role and permissions, not on visibleApps, which is a new array every render
  }, [role, permissions.join(",")]);

  // Mission Control — the swipe-up overview of every open window.
  const [mission, setMission] = useState(false);
  useShortcut({ combo: "mod+arrowup", scope: "global", label: "Mission Control", run: () => setMission((v) => !v) }, []);
  useEffect(() => {
    if (!mission) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setMission(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mission]);
  // Close Mission Control automatically once no windows remain.
  useEffect(() => {
    if (mission && shell.stack.length === 0) setMission(false);
  }, [mission, shell.stack.length]);

  return (
    <div className="rdr-desktop" data-workspace="engine" data-wall-tone="dark">
      <div className="rdr-wall" aria-hidden="true" />
      {!isMobile && <MenuBar focusedApp={focusedApp?.name ?? "GoGamo Radar"} />}

      {/* macOS-style desktop icons down the right edge (desktop only). */}
      {!isMobile && <DesktopIcons apps={visibleApps} />}

      {isMobile ? (
        <RadarPhone apps={visibleApps} windows={shell.windows} stack={shell.stack} mobile={mobile} />
      ) : (
        <div className="rdr-winlayer">
          {/* A stable DOM order, stacked by z-index (2026-09-21). Rendering in stack order moved the focused window's
              node to the end on every press, and a moved node loses its pointer capture, so a drag that began on a
              window behind another dropped it as soon as the cursor left the title bar. */}
          {Object.keys(shell.windows).map((id) => {
            const index = shell.stack.indexOf(id);
            const win = shell.windows[id];
            if (!win) return null;
            const def = appById(win.appId);
            if (!def) return null;
            return (
              <WindowFrame key={id} win={win} zIndex={index + 10} focused={id === focusedId}>
                <Suspense fallback={<ShellSpinner />}>
                  <def.Component windowId={id} launchParams={win.launchParams} />
                </Suspense>
              </WindowFrame>
            );
          })}
        </div>
      )}

      {mission && !isMobile && <MissionControl windows={shell.windows} stack={shell.stack} onClose={() => setMission(false)} />}

      {!isMobile && <Dock apps={visibleApps} windows={shell.windows} onMission={() => setMission((v) => !v)} />}

      {createPortal(
        <div className="rdr-toasts">
          {shell.toasts.map((t) => (
            <button key={t.id} type="button" className={`rdr-toast ${t.kind}`} onClick={() => dismissToast(t.id)}>
              <span className="t-ic" aria-hidden="true">
                <RadarMark size={15} />
              </span>
              <span className="min-w-0 text-left">
                <span className="t-title block">{t.title}</span>
                {t.body && <span className="t-body block">{t.body}</span>}
              </span>
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
}

/* dock shortcut registration, imperative because count is role-dependent */
function registerDockShortcut(def: RadarAppDefinition, index: number) {
  return registerShortcut({
    combo: `mod+${index + 1}`,
    scope: "global",
    label: `Open ${def.name}`,
    run: () => launchApp(def),
  });
}

function ShellSpinner() {
  return (
    <div className="grid h-full place-items-center">
      <span className="text-sm text-ink-tertiary">Loading…</span>
    </div>
  );
}

function useDockOrder() {
  return useSyncExternalStore(subscribeDock, getDockOrder);
}

function Dock({
  apps,
  windows,
  onMission,
}: {
  apps: RadarAppDefinition[];
  windows: Record<string, WindowState>;
  onMission: () => void;
}) {
  const appLabel = useAppLabel();
  useDockOrder(); // re-render when the pinned set/order changes
  const running = new Set(Object.values(windows).map((w) => w.appId));
  const minimized = Object.values(windows).filter((w) => w.minimized);
  const openCount = Object.keys(windows).length;

  const visibleIds = apps.map((a) => a.id);
  const dockApps = resolveDockIds(visibleIds)
    .map((id) => apps.find((a) => a.id === id))
    .filter((a): a is RadarAppDefinition => Boolean(a));
  const addable = addableDockIds(visibleIds)
    .map((id) => apps.find((a) => a.id === id))
    .filter((a): a is RadarAppDefinition => Boolean(a));

  const dragId = useRef<string | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [adderOpen, setAdderOpen] = useState(false);
  const dockRef = useRef<HTMLElement | null>(null);

  /* ONE line, always: adding another icon must not overflow the pill. Measured
     against the real element (icon widths vary with labels/locale), with one
     item's width of headroom for the icon being added. */
  const canFitOneMore = () => {
    const el = dockRef.current;
    if (!el) return true;
    const item = el.querySelector<HTMLElement>(".rdr-dock-item");
    const itemW = (item?.offsetWidth ?? 44) + 6; /* + gap */
    const max = Math.min(window.innerWidth - 32, Number.MAX_SAFE_INTEGER);
    return el.scrollWidth + itemW <= max;
  };
  const guardedAdd = (id: string) => {
    if (!canFitOneMore()) {
      pushToast({
        kind: "err",
        title: "The dock is full",
        body: "One line only. Right-click an icon to remove it, then add this one.",
      });
      return;
    }
    addToDock(id, visibleIds);
  };

  return (
    <nav className="rdr-dock" aria-label="Dock" ref={dockRef}>
      {dockApps.map((def) => (
        <button
          key={def.id}
          type="button"
          className={"rdr-dock-item" + (draggingId === def.id ? " dragging" : "")}
          draggable
          onDragStart={(e) => {
            dragId.current = def.id;
            setDraggingId(def.id);
            e.dataTransfer.effectAllowed = "move";
          }}
          onDragEnter={() => {
            if (dragId.current && dragId.current !== def.id) {
              reorderDock(dragId.current, def.id, visibleIds);
            }
          }}
          onDragOver={(e) => e.preventDefault()}
          onDragEnd={() => {
            dragId.current = null;
            setDraggingId(null);
          }}
          onClick={() => launchApp(def)}
          onContextMenu={(e) => {
            e.preventDefault();
            setMenu({ id: def.id, x: e.clientX, y: e.clientY });
          }}
          aria-label={def.name}
        >
          <span className={"icon" + (def.id === "about" ? " neutral" : "")}>{def.icon}</span>
          <span className="label">{appLabel(def.id, def.name)}</span>
          {running.has(def.id) && <span className="running" aria-hidden="true" />}
        </button>
      ))}

      {/* Add / edit the dock */}
      <span className="rdr-dock-sep" aria-hidden="true" />
      <div className="relative">
        <button
          type="button"
          className="rdr-dock-item"
          onClick={() => setAdderOpen((v) => !v)}
          aria-label="Edit dock"
        >
          <span className="icon neutral">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <path d="M12 6v12M6 12h12" />
            </svg>
          </span>
          <span className="label">Edit dock</span>
        </button>
        {adderOpen && (
          <DockAdder
            addable={addable}
            canReset={getDockOrder() !== null}
            onAdd={guardedAdd}
            onReset={() => {
              resetDock();
              setAdderOpen(false);
            }}
            onClose={() => setAdderOpen(false)}
          />
        )}
      </div>

      {menu &&
        createPortal(
          <DockContextMenu
            x={menu.x}
            y={menu.y}
            onRemove={() => {
              removeFromDock(menu.id, visibleIds);
              setMenu(null);
            }}
            onClose={() => setMenu(null)}
          />,
          document.body,
        )}

      {minimized.length > 0 && <span className="rdr-dock-sep" aria-hidden="true" />}
      {minimized.map((win) => {
        const def = appById(win.appId);
        return (
          <button
            key={win.id}
            type="button"
            className="rdr-dock-item"
            onClick={() => restoreWindow(win.id)}
            aria-label={`Restore ${win.title}`}
          >
            <span className="icon neutral">{def?.icon}</span>
            <span className="label">{win.title}</span>
          </button>
        );
      })}
      {/* Mission Control — every open window in one grid (⌘↑). */}
      <span className="rdr-dock-sep" aria-hidden="true" />
      <button type="button" className="rdr-dock-item" onClick={onMission} aria-label="Mission Control">
        <span className="icon neutral">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3.5" y="4" width="7" height="6" rx="1.3" /><rect x="13.5" y="4" width="7" height="6" rx="1.3" />
            <rect x="3.5" y="14" width="7" height="6" rx="1.3" /><rect x="13.5" y="14" width="7" height="6" rx="1.3" />
          </svg>
        </span>
        <span className="label">Mission Control</span>
        {openCount > 0 && <span className="running" aria-hidden="true" />}
      </button>
    </nav>
  );
}

/** The small popover for adding apps back to the dock (and resetting it). */
function DockAdder({
  addable,
  canReset,
  onAdd,
  onReset,
  onClose,
}: {
  addable: RadarAppDefinition[];
  canReset: boolean;
  onAdd: (id: string) => void;
  onReset: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", close);
    return () => window.removeEventListener("pointerdown", close);
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="absolute bottom-16 left-1/2 z-[950] w-60 -translate-x-1/2 rounded-xl p-1.5 text-ink shadow-xl"
      style={{ background: "color-mix(in srgb, var(--gg-surface-raised) 96%, transparent)", backdropFilter: "blur(16px)" }}
    >
      <p className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-tertiary">Add to dock</p>
      {addable.length === 0 ? (
        <p className="px-3 py-1.5 text-[12.5px] text-ink-tertiary">Every app is already in your dock.</p>
      ) : (
        addable.map((def) => (
          <button
            key={def.id}
            type="button"
            className="flex w-full items-center gap-2.5 rounded-lg px-3 py-1.5 text-left text-[13px] font-medium hover:bg-surface-sunken"
            onClick={() => onAdd(def.id)}
          >
            <span className="grid h-5 w-5 place-items-center text-brand">{def.icon}</span>
            {def.name}
          </button>
        ))
      )}
      <p className="mt-1 px-3 py-1 text-[11px] leading-snug text-ink-tertiary">
        Drag icons to reorder. Right click an icon to remove it.
      </p>
      {canReset && (
        <button
          type="button"
          className="mt-0.5 block w-full rounded-lg px-3 py-1.5 text-left text-[12.5px] font-medium text-ink-secondary hover:bg-surface-sunken"
          onClick={onReset}
        >
          Reset to default
        </button>
      )}
    </div>
  );
}

/** Right click menu on a dock icon: remove it from the dock. */
function DockContextMenu({
  x,
  y,
  onRemove,
  onClose,
}: {
  x: number;
  y: number;
  onRemove: () => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    window.addEventListener("pointerdown", close);
    window.addEventListener("blur", onClose);
    return () => {
      window.removeEventListener("pointerdown", close);
      window.removeEventListener("blur", onClose);
    };
  }, [onClose]);
  return (
    <div
      ref={ref}
      className="fixed z-[960] min-w-40 rounded-lg p-1 text-ink shadow-xl"
      style={{
        left: Math.min(x, window.innerWidth - 180),
        top: Math.max(8, y - 44),
        background: "color-mix(in srgb, var(--gg-surface-raised) 97%, transparent)",
        backdropFilter: "blur(16px)",
      }}
    >
      <button
        type="button"
        className="block w-full rounded-md px-3 py-1.5 text-left text-[13px] font-medium hover:bg-surface-sunken"
        onClick={onRemove}
      >
        Remove from dock
      </button>
    </div>
  );
}

/** macOS-style desktop shortcuts down the right edge — click to open. */
function DesktopIcons({ apps }: { apps: RadarAppDefinition[] }) {
  const appLabel = useAppLabel();
  return (
    <div className="rdr-desk-icons" aria-label="Desktop apps">
      {apps.map((def) => (
        <button key={def.id} type="button" className="rdr-desk-icon" onDoubleClick={() => launchApp(def)} onClick={() => launchApp(def)} aria-label={appLabel(def.id, def.name)}>
          <span className="ic">{def.icon}</span>
          <span className="nm">{appLabel(def.id, def.name)}</span>
        </button>
      ))}
    </div>
  );
}

/**
 * Mission Control — the swipe-up overview. Every open window as a card; click to
 * focus it, × to close it. Backdrop or Esc dismisses.
 */
function MissionControl({
  windows,
  stack,
  onClose,
}: {
  windows: Record<string, WindowState>;
  stack: string[];
  onClose: () => void;
}) {
  const open = stack.filter((id) => windows[id]);
  return createPortal(
    <div className="rdr-mission" onClick={onClose}>
      <p className="rdr-mission-title">Mission Control · {open.length} open</p>
      {open.length === 0 ? (
        <p className="rdr-mission-empty">No open windows. Open an app from the dock or desktop.</p>
      ) : (
        <div className="rdr-mission-grid" onClick={(e) => e.stopPropagation()}>
          {open
            .slice()
            .reverse()
            .map((id) => {
              const win = windows[id]!;
              const def = appById(win.appId);
              return (
                <div
                  key={id}
                  className="rdr-mission-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => {
                    restoreWindow(id);
                    focusWindow(id);
                    onClose();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      restoreWindow(id);
                      focusWindow(id);
                      onClose();
                    }
                  }}
                >
                  <button
                    type="button"
                    className="rdr-mission-close"
                    aria-label={`Close ${win.title}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      closeWindow(id);
                    }}
                  >
                    <svg viewBox="0 0 12 12" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 3l6 6M9 3l-6 6" /></svg>
                  </button>
                  <span className="mc-ic">{def?.icon}</span>
                  <span className="mc-title">{win.title}</span>
                  {win.minimized && <span className="mc-min">minimized</span>}
                </div>
              );
            })}
        </div>
      )}
    </div>,
    document.body,
  );
}
